// biome-ignore-all lint/a11y/useSemanticElements: <div role="dialog"> wraps a
//   non-form celebration overlay. Promoting it to <dialog> would require
//   imperative .showModal() lifecycle and conflict with the existing
//   conditional-render shape (the parent MatchScreen mounts/unmounts based
//   on terminal status). role="dialog" + aria-modal preserves the
//   accessibility contract.
//
// Phase 17 Plan 05 Task 2 — WinOverlay end-of-match celebration.
//
// Mounted when the match resolves in the local player's favor. The DOM tree
// matches UI-SPEC §6.2 + rg-match.jsx WinOverlay (lines 394-462):
//   - 100px WIN! display-word gradient heading
//   - streakCount >= 2 → ★ {N}-MATCH WINNING STREAK ★ yellow ribbon
//   - Manrope 14 italic flavor quote (i18n: match.win.flavor_quote)
//   - Single centered XP reward tile (#13 — GDD §9.1 only commits to XP;
//     coins/gems/words have no backend implementation and no GDD support,
//     so the speculative 4-cell grid from the rg-match.jsx prototype has
//     been pared down to the one currency that actually ships)
//   - Optional cyan "WATCH AD · REWARDS ×2" Btn (adsEnabled=true; default
//     hidden in v1.1 per DEC-025 — ads ship post-launch)
//   - 50/50 HOME (blue) + REPLAY (yellow) Btn row
//
// Unlike FlopReveal/QueenRises, this overlay does NOT auto-dismiss — the
// user must explicitly tap HOME or REPLAY. Keyboard users get a default
// focus on HOME (the safer of the two CTAs).

import { useStore } from '@nanostores/react'
import { type JSX, useCallback, useEffect, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { playSfx } from '../../game/audio/useAudio'
import { $affinityReveal } from '../../stores/affinityReveal'
import { effectiveBearerJwt } from '../../stores/anonSession'
import { $reducedMotion } from '../../stores/motion'
import { $scoreReveal } from '../../stores/scoreReveal'
import { Btn } from '../components/Btn'
import { FolkStar } from '../components/Icon'
import { useFocusTrap } from '../components/useFocusTrap'
import { LevelUpModal } from './LevelUpModal'

export interface WinOverlayRewards {
  readonly xp: number
}

/**
 * Plan 20-06 META-06 — optional level-up payload. When present (i.e., the
 * caller has determined the player crossed a level threshold this match)
 * the WinOverlay renders the LevelUpModal at the end of its JSX. The
 * caller — typically MatchScreen — derives this from the diff between
 * the pre-match `/api/profile/me` fetch (level + inventory) and the
 * post-match one. When omitted/null the modal is not rendered.
 */
export interface WinOverlayLevelUpData {
  readonly newLevel: number
  readonly unlockedCosmeticIds: ReadonlyArray<string>
}

export interface WinOverlayProps {
  /**
   * Polish Phase 4 (CINE-03/04, SCREEN-01) — match outcome. Defaults to 'win'
   * so every existing caller + test renders the unchanged WIN! screen. 'lose'
   * and 'draw' render the designed §9.8 end-states (LOSE keeps the warm-yellow
   * stroke but drops the celebratory gradient; both get an opponent/neutral
   * edge sweep + the matching sting cue) instead of the old flat scrim.
   *
   * 'complete' is the defensive fallback when winner_slot is absent; outcome
   * derives from winner_slot via deriveTerminalOutcome (CINE-1).
   */
  readonly outcome?: 'win' | 'lose' | 'draw' | 'complete'
  readonly onHome: () => void
  readonly onReplay: () => void
  readonly rewards: WinOverlayRewards
  readonly streakCount: number
  readonly adsEnabled?: boolean
  /** Plan 20-06 META-06 — when set, LevelUpModal renders at end of JSX. */
  readonly levelUp?: WinOverlayLevelUpData | null
  /**
   * Plan 20-07 META-07 — when set, the SHARE CTA fetches
   * GET /api/match/{matchId}/share-image and routes the PNG blob to
   * navigator.share (mobile) / clipboard (desktop) / download (fallback).
   * When omitted, the SHARE button is not rendered.
   */
  readonly matchId?: string
  /**
   * Phase 31 Plan 31-04 RUNE-33 — caller's slot (0 or 1). When the
   * end-of-match `_affinity_reveal` sidecar arrives via the
   * `$affinityReveal` atom, this slot determines which side of the {a,b}
   * tuple is "own" vs "opp" for the reveal block. Optional for backward
   * compatibility — when omitted (or no reveal atom set), the affinity
   * reveal block does not render.
   */
  readonly yourSlot?: 0 | 1
  /**
   * Phase 31 Plan 31-04 RUNE-33 — caller's 4-letter affinity tuple from
   * `PlayerView.your_affinity_letters`. Threaded down from MatchScreen so
   * the WinOverlay reveal block can show the "own" row pre-animated and
   * cross-fade it to full brightness during the reveal sequence.
   */
  readonly yourAffinityLetters?: readonly [string, string, string, string]
}

type ShareState = 'idle' | 'sharing' | 'success' | 'failed'

interface RewardCell {
  readonly key: keyof WinOverlayRewards
  readonly label: string
  readonly variant: 'yellow' | 'pink' | 'cyan'
}

const REWARD_CELLS: ReadonlyArray<RewardCell> = [
  { key: 'xp', label: 'XP', variant: 'yellow' },
]

export function WinOverlay({
  outcome = 'win',
  onHome,
  onReplay,
  rewards,
  streakCount,
  adsEnabled = false,
  levelUp = null,
  matchId,
  yourSlot,
  yourAffinityLetters,
}: WinOverlayProps): JSX.Element {
  const isWin = outcome === 'win'
  const titleId =
    outcome === 'lose'
      ? 'match.end.lose.title'
      : outcome === 'draw'
        ? 'match.end.draw.title'
        : outcome === 'complete'
          ? 'match.end.complete.title'
          : 'match.end.win.title'
  const titleDefault =
    outcome === 'lose'
      ? 'LOSE'
      : outcome === 'draw'
        ? 'DRAW'
        : outcome === 'complete'
          ? 'MATCH COMPLETE'
          : 'WIN!'
  const flavorId =
    outcome === 'lose'
      ? 'match.end.lose.flavor'
      : outcome === 'draw'
        ? 'match.end.draw.flavor'
        : outcome === 'complete'
          ? 'match.end.complete.flavor'
          : 'match.win.flavor_quote'
  const flavorDefault =
    outcome === 'lose'
      ? 'This board belongs to them — for now.'
      : outcome === 'draw'
        ? 'Evenly matched. The runes are silent.'
        : outcome === 'complete'
          ? 'The gambit is settled. Return to the hall for the verdict.'
          : 'A worthy gambit. The runes remember.'
  // Theme-token edge-sweep accent (§12.3 — winner/opponent colour). WIN keeps
  // its exact existing background so the captured win-overlay baselines stay
  // pixel-stable; lose tints destructive; draw + the neutral `complete`
  // end-state share the neutral magic tint (THEME-04).
  const accentVar = isWin
    ? '--color-cta-primary'
    : outcome === 'lose'
      ? '--color-cta-destructive'
      : '--color-magic'
  const intl = useIntl()
  // Phase 31 Plan 31-04 (RUNE-33) — terminal affinity reveal sidecar
  // subscription. When the atom carries a payload AND we have the caller's
  // own letters, we render the reveal block between the flavor quote and
  // the reward grid; the reward grid is gated on revealComplete (1.8s
  // standard / 400ms reduced — Pitfall 7 hard timeout).
  const affinityReveal = useStore($affinityReveal)
  const reducedMotion = useStore($reducedMotion)
  // ARCA-152 — terminal score + captures reveal sidecar. When the atom carries
  // a payload AND we know the caller's slot, render the final score + captures
  // breakdown (own vs opponent derived from `yourSlot`). The match is won by
  // king-capture / turn-cap / stalemate — this is honest post-match feedback,
  // not the win determinant, so it is labelled "Final score".
  const scoreReveal = useStore($scoreReveal)
  const showScoreBreakdown = scoreReveal !== null && yourSlot !== undefined
  const ownSummary = !showScoreBreakdown
    ? null
    : yourSlot === 0
      ? scoreReveal.a
      : scoreReveal.b
  const oppSummary = !showScoreBreakdown
    ? null
    : yourSlot === 0
      ? scoreReveal.b
      : scoreReveal.a

  // End-state cue on mount (audio gated on mute only, plays under reduced
  // motion): bright fanfare on a win, a somber sting on a loss, a soft neutral
  // pulse on a draw.
  useEffect(() => {
    playSfx(
      outcome === 'win'
        ? 'win-fanfare'
        : outcome === 'lose'
          ? 'lose-sting'
          : 'reveal-pulse',
    )
  }, [outcome])
  const showRevealBlock =
    affinityReveal !== null &&
    yourSlot !== undefined &&
    yourAffinityLetters !== undefined
  const [revealComplete, setRevealComplete] = useState<boolean>(
    !showRevealBlock,
  )
  useEffect(() => {
    if (!showRevealBlock) {
      setRevealComplete(true)
      return undefined
    }
    // T-31-07 hard timeout — fires onComplete regardless of CSS animation
    // state. Reduced motion: 200ms cross-fade per row + 200ms slack ≈ 400ms.
    // Standard: 200ms per chip × 100ms stagger × 8 chips ≈ 1.5s + 300ms slack.
    const ms = reducedMotion ? 400 : 1800
    const handle = window.setTimeout(() => setRevealComplete(true), ms)
    return () => window.clearTimeout(handle)
  }, [showRevealBlock, reducedMotion])
  // Phase 18 POLISH-06 — focus trap + initial focus + Escape-to-home.
  // WinOverlay has no explicit close button; Escape routes to onHome
  // (the safer of the two CTAs — first focus target). The focus trap is
  // active only when the LevelUpModal is NOT showing — otherwise the
  // modal owns the trap.
  const dialogRef = useRef<HTMLDivElement>(null)
  // Plan 20-06 — local state for the LevelUpModal lifecycle. Opens to true
  // when `levelUp` is supplied at mount; dismiss flips to false and the
  // modal returns null (preserving the celebratory one-shot behavior).
  const [showLevelUp, setShowLevelUp] = useState<boolean>(levelUp !== null)
  // We track whether we've already opened the modal once for this WinOverlay
  // instance so a post-dismiss prop refresh does NOT re-open it (the user
  // already dismissed). The state machine: "modal pending" → "modal showing"
  // → "modal dismissed" (the dismissed branch never returns to pending).
  const hasShownLevelUpRef = useRef<boolean>(levelUp !== null)
  useEffect(() => {
    // If the prop later becomes available (e.g., post-match fetch resolves
    // after WinOverlay mount), open the modal once.
    if (levelUp !== null && !hasShownLevelUpRef.current) {
      hasShownLevelUpRef.current = true
      setShowLevelUp(true)
    }
  }, [levelUp])
  useFocusTrap(dialogRef, !showLevelUp, onHome)

  // Plan 20-07 META-07 — SHARE CTA state + handler. The share-image
  // endpoint returns binary `image/png` so we use raw `fetch` (apiFetch
  // is JSON-only). Bearer is read fresh from the auth atom on click;
  // failure modes (401/403/404/410/network) all funnel to a single
  // visible feedback line so the user can retry. The chain is
  // navigator.share → clipboard → download — first available branch
  // wins. Plan 20-05 inline-PNG endpoint precedent (D-019-T2-A).
  const [shareState, setShareState] = useState<ShareState>('idle')
  const [shareMessageKey, setShareMessageKey] = useState<string | null>(null)
  const handleShare = useCallback(async () => {
    if (typeof matchId !== 'string' || matchId.length === 0) return
    setShareState('sharing')
    setShareMessageKey(null)
    try {
      const apiBase = import.meta.env?.VITE_API_BASE_URL ?? ''
      // ARCA-93: real bearer if authenticated, else the anon practice JWT so a
      // guest can share their bot-match result image too.
      const jwt = effectiveBearerJwt()
      // CR-02 fix (Phase 20 review): the share-image endpoint returns
      // `image/png` on success — advertise that we accept it. The server is
      // exempt from the v1 JSON Accept gate for this endpoint; the previous
      // `application/vnd.runegambit.v1+json` value was a content-negotiation
      // contradiction (Accept advertising JSON while consuming a PNG blob).
      const headers: Record<string, string> = {
        Accept: 'image/png',
      }
      if (jwt) headers.Authorization = `Bearer ${jwt}`
      const res = await fetch(`${apiBase}/api/match/${matchId}/share-image`, {
        method: 'GET',
        headers,
      })
      if (!res.ok) {
        setShareState('failed')
        setShareMessageKey('match.share_failed')
        return
      }
      const blob = await res.blob()
      const file =
        typeof File === 'function'
          ? new File([blob], `match-${matchId}.png`, { type: 'image/png' })
          : null
      const nav = navigator as Navigator & {
        canShare?: (data: ShareData) => boolean
        share?: (data: ShareData) => Promise<void>
      }
      if (
        file !== null &&
        typeof nav.share === 'function' &&
        typeof nav.canShare === 'function' &&
        nav.canShare({ files: [file] })
      ) {
        await nav.share({ files: [file] })
        setShareState('success')
        return
      }
      // Desktop fallback — clipboard
      const clip = navigator.clipboard as
        | (Clipboard & { write?: (items: ClipboardItem[]) => Promise<void> })
        | undefined
      const ClipboardItemCtor = (
        window as unknown as { ClipboardItem?: typeof ClipboardItem }
      ).ClipboardItem
      if (clip && typeof clip.write === 'function' && ClipboardItemCtor) {
        try {
          await clip.write([new ClipboardItemCtor({ 'image/png': blob })])
          setShareState('success')
          setShareMessageKey('match.share_copied')
          return
        } catch {
          // Fall through to download fallback.
        }
      }
      // Final fallback — programmatic download anchor.
      // WR-04 fix (Phase 20 review): wrap click + revokeObjectURL in
      // try/finally so a synchronous throw inside `a.click()` (Safari's
      // "user gesture lost" path can do this) does NOT leak the blob URL
      // and an orphan anchor in the DOM. The previous version called
      // `revokeObjectURL` AFTER `click()`, so an exception bypassed the
      // cleanup entirely and left the blob URL allocated until tab
      // unload — measurable in DevTools as a Memory Leak entry.
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `match-${matchId}.png`
      document.body.appendChild(a)
      try {
        a.click()
      } finally {
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      }
      setShareState('success')
      setShareMessageKey('match.share_downloaded')
    } catch (_err) {
      setShareState('failed')
      setShareMessageKey('match.share_failed')
    }
  }, [matchId])

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="win-heading"
      className="end-overlay"
      data-screen-region="end-of-match"
      data-outcome={outcome}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 70,
        padding: '60px var(--space-5) var(--space-6)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 'var(--space-4)',
        background: isWin
          ? 'radial-gradient(circle at 50% 35%, color-mix(in srgb, var(--color-cta-primary) 35%, transparent) 0%, transparent 60%), linear-gradient(180deg, var(--color-paper-2), var(--color-paper))'
          : `radial-gradient(circle at 50% 35%, color-mix(in srgb, var(${accentVar}) 28%, transparent) 0%, transparent 60%), linear-gradient(180deg, var(--color-paper-2), var(--color-paper))`,
      }}
    >
      <h1
        id="win-heading"
        className={isWin ? 'display-word gradient' : 'display-word'}
        style={{
          fontFamily: 'var(--font-family-display)',
          fontWeight: 700,
          // 'complete' is a two-word heading; drop to 52px so "MATCH COMPLETE"
          // wraps gracefully instead of overflowing narrow viewports at 100px.
          fontSize:
            outcome === 'complete'
              ? 'var(--font-size-52)'
              : 'var(--font-size-100)',
          letterSpacing: '-0.04em',
          margin: 0,
          ...(outcome === 'complete'
            ? { textAlign: 'center' as const, lineHeight: 1.05 }
            : {}),
        }}
      >
        <FormattedMessage id={titleId} defaultMessage={titleDefault} />
      </h1>

      {isWin && streakCount >= 2 ? (
        <div
          className="ribbon yellow"
          style={{
            padding: 'var(--space-1) var(--space-5)',
            background: 'var(--color-cta-primary)',
            color: 'var(--color-inkwell)',
            borderRadius: 'var(--radius-pill)',
            fontFamily: 'var(--font-family-display)',
            fontWeight: 700,
            fontSize: 'var(--font-size-13)',
          }}
        >
          <FormattedMessage
            id="match.end.streak"
            defaultMessage="★ {count}-match winning streak ★"
            values={{ count: streakCount }}
          />
        </div>
      ) : null}

      <p
        style={{
          fontStyle: 'italic',
          fontFamily: 'var(--font-family-body)',
          fontSize: 'var(--font-size-14)',
          color: 'var(--color-text-secondary)',
          textAlign: 'center',
          margin: 0,
        }}
      >
        <FormattedMessage id={flavorId} defaultMessage={flavorDefault} />
      </p>

      {showScoreBreakdown && ownSummary !== null && oppSummary !== null ? (
        <section
          data-screen-region="end-score-breakdown"
          data-testid="win-score-breakdown"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-2)',
            alignItems: 'center',
            margin: 'var(--space-2) 0',
          }}
        >
          <h2
            style={{
              fontFamily: 'var(--font-family-display)',
              fontWeight: 700,
              fontSize: 'var(--font-size-14)',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              color: 'var(--color-text-secondary)',
              margin: 0,
            }}
          >
            <FormattedMessage
              id="match.end.score.title"
              defaultMessage="Final score"
            />
          </h2>
          <div
            data-screen-region="end-score-row"
            style={{
              display: 'flex',
              gap: 'var(--space-5)',
              alignItems: 'baseline',
              fontFamily: 'var(--font-family-display)',
              fontWeight: 700,
              fontSize: 'var(--font-size-22)',
              fontVariantNumeric: 'tabular-nums',
              color: 'var(--color-text-primary)',
            }}
          >
            <span data-testid="win-score-you">
              <FormattedMessage
                id="match.end.score.you"
                defaultMessage="You {score}"
                values={{ score: ownSummary.score }}
              />
            </span>
            <span data-testid="win-score-opponent">
              <FormattedMessage
                id="match.end.score.opponent"
                defaultMessage="Opponent {score}"
                values={{ score: oppSummary.score }}
              />
            </span>
          </div>
          <p
            data-testid="win-captures-breakdown"
            style={{
              fontFamily: 'var(--font-family-body)',
              fontWeight: 600,
              fontSize: 'var(--font-size-13)',
              color: 'var(--color-text-secondary)',
              margin: 0,
              textAlign: 'center',
            }}
          >
            <FormattedMessage
              id="match.end.captures.breakdown"
              defaultMessage="Captures — you {you}, opponent {opponent}"
              values={{
                you: ownSummary.captures,
                opponent: oppSummary.captures,
              }}
            />
          </p>
        </section>
      ) : null}

      {showRevealBlock &&
      affinityReveal !== null &&
      yourSlot !== undefined &&
      yourAffinityLetters !== undefined ? (
        <AffinityRevealBlock
          ownLetters={yourAffinityLetters}
          oppLetters={
            (yourSlot === 0 ? affinityReveal.b : affinityReveal.a) as readonly [
              string,
              string,
              string,
              string,
            ]
          }
          reducedMotion={reducedMotion}
        />
      ) : null}

      {revealComplete ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 'var(--space-3)',
            width: '100%',
            maxWidth: 300,
          }}
        >
          {REWARD_CELLS.map((cell) => (
            <div
              key={cell.key}
              className="sub-card"
              style={{
                padding: 'var(--space-4) var(--space-6)',
                background: 'var(--color-surface-mid)',
                border: '3px solid var(--color-surface-stroke)',
                borderRadius: 'var(--radius-md)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 'var(--space-1)',
                minWidth: 120,
              }}
            >
              <span className={`icon-tile ${cell.variant}`} aria-hidden="true">
                <FolkStar size={18} color="currentColor" />
              </span>
              <span
                className="fd"
                style={{
                  fontFamily: 'var(--font-family-display)',
                  fontWeight: 700,
                  fontSize: 'var(--font-size-18)',
                  color: 'var(--color-text-primary)',
                }}
              >
                +{rewards[cell.key]}
              </span>
              <span
                style={{
                  fontSize: 'var(--font-size-12)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  color: 'var(--color-text-secondary)',
                }}
              >
                {cell.label}
              </span>
            </div>
          ))}
        </div>
      ) : null}

      {adsEnabled ? (
        <Btn kind="them" size="md">
          WATCH AD · REWARDS ×2
        </Btn>
      ) : null}

      <div
        style={{
          display: 'flex',
          gap: 'var(--space-3)',
          width: '100%',
          maxWidth: 320,
          marginTop: 'auto',
        }}
      >
        <div style={{ flex: 1 }}>
          <Btn kind="primary" size="md" block onClick={onHome}>
            HOME
          </Btn>
        </div>
        <div style={{ flex: 1 }}>
          <Btn kind="ghost" size="md" block onClick={onReplay}>
            REPLAY
          </Btn>
        </div>
      </div>

      {/* Plan 20-07 META-07 — SHARE CTA. Renders only when a matchId is
          supplied (the share-image endpoint targets a specific match).
          Sits as the THIRD CTA (after HOME + REPLAY) in a separate row.
          Disabled mid-fetch to prevent duplicate share attempts. */}
      {typeof matchId === 'string' && matchId.length > 0 ? (
        <div
          style={{ width: '100%', maxWidth: 320 }}
          data-screen-region="share-cta"
        >
          <Btn
            kind="ghost"
            size="md"
            block
            disabled={shareState === 'sharing'}
            ariaLabel={intl.formatMessage({ id: 'match.share_button' })}
            onClick={() => {
              void handleShare()
            }}
            testId="win-overlay-share-button"
          >
            {intl.formatMessage({ id: 'match.share_button' })}
          </Btn>
          {shareMessageKey !== null ? (
            <p
              role="status"
              aria-live="polite"
              className="share-feedback"
              data-share-state={shareState}
              style={{
                marginTop: 'var(--space-2)',
                fontSize: 'var(--font-size-13)',
                fontFamily: 'var(--font-family-body)',
                color:
                  shareState === 'failed'
                    ? 'var(--color-cta-destructive)'
                    : 'var(--color-text-secondary)',
                textAlign: 'center',
              }}
            >
              {intl.formatMessage({ id: shareMessageKey })}
            </p>
          ) : null}
        </div>
      ) : null}

      {/* Plan 20-06 META-06 — LevelUpModal renders LAST in WinOverlay JSX
          so Plan 20-07's SHARE-button addition can land in the CTA cluster
          ABOVE without merge conflict. */}
      {levelUp !== null && showLevelUp ? (
        <LevelUpModal
          open={showLevelUp}
          onClose={() => setShowLevelUp(false)}
          newLevel={levelUp.newLevel}
          unlockedCosmeticIds={levelUp.unlockedCosmeticIds}
        />
      ) : null}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Phase 31 Plan 31-04 — AffinityRevealBlock (RUNE-33).
// ---------------------------------------------------------------------------
//
// Renders the 1.5s end-of-match affinity reveal sequence between the WIN/LOSE
// display word + flavor quote and the reward grid. Two rows of 4 chips each:
//
//   own row: caller's `your_affinity_letters` (already-known to the caller —
//            this row animates brightness 0.85 → 1.0 to land in the "now
//            everyone knows your runes" moment).
//   opp row: opponent's affinity letters, surfaced via the terminal
//            `_affinity_reveal` sidecar (atom-driven). Animates α 0 → 1.
//
// Reveal timing:
//   Standard motion (UI-SPEC §5 row 5): per-chip 200ms ease-bouncy α/brightness
//     ramp, 100ms stagger × 8 chips → ~600ms per row + 400ms row offset = ~1s
//     visible motion, well within the 1.5s narrative window. Pixi/DOM hybrid
//     handled via CSS animation keyframes (own/opp variants).
//   Reduced motion (UI-SPEC §5 row 5): 200ms linear cross-fade per row,
//     simultaneous (no stagger). Total animation ≤ 200ms.
//
// Reward-grid unblocking: handled by WinOverlay's `revealComplete` state
// driven by a hard setTimeout (T-31-07 lock — fires regardless of CSS animation
// state so a stuck keyframe can't hang the celebration).

interface AffinityRevealBlockProps {
  readonly ownLetters: readonly [string, string, string, string]
  readonly oppLetters: readonly [string, string, string, string]
  readonly reducedMotion: boolean
}

function AffinityRevealBlock({
  ownLetters,
  oppLetters,
  reducedMotion,
}: AffinityRevealBlockProps): JSX.Element {
  return (
    <section
      data-screen-region="win-affinity-reveal"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-4)',
        margin: 'var(--space-5) 0',
        alignItems: 'center',
      }}
    >
      <RevealChipRow
        letters={ownLetters}
        side="own"
        reducedMotion={reducedMotion}
      />
      {/* AFFINITY-06 — restate the +1 affinity rule the player just lived
          (no new mechanic; reinforces why their 4 runes mattered). */}
      <p
        data-screen-region="win-affinity-recap"
        style={{
          fontFamily: 'var(--font-family-body)',
          fontWeight: 600,
          fontSize: 'var(--font-size-13)',
          color: 'var(--color-text-secondary)',
          margin: 0,
          textAlign: 'center',
        }}
      >
        <FormattedMessage id="match.win.affinity_recap" />
      </p>
      <RevealChipRow
        letters={oppLetters}
        side="opp"
        reducedMotion={reducedMotion}
      />
      <p
        style={{
          fontFamily: 'var(--font-family-body)',
          fontWeight: 600,
          fontSize: 'var(--font-size-14)',
          color: 'var(--color-text-secondary)',
          margin: 0,
          textAlign: 'center',
        }}
      >
        <FormattedMessage
          id="match.win.affinity_reveal_subtitle"
          values={{
            a: oppLetters[0],
            b: oppLetters[1],
            c: oppLetters[2],
            d: oppLetters[3],
          }}
        />
      </p>
    </section>
  )
}

interface RevealChipRowProps {
  readonly letters: readonly [string, string, string, string]
  readonly side: 'own' | 'opp'
  readonly reducedMotion: boolean
}

function RevealChipRow({
  letters,
  side,
  reducedMotion,
}: RevealChipRowProps): JSX.Element {
  const startDelay = side === 'opp' && !reducedMotion ? 400 : 0
  return (
    <div
      data-reveal-row={side}
      style={{
        display: 'flex',
        gap: 'var(--space-2)',
        justifyContent: 'center',
      }}
    >
      {letters.map((letter, idx) => {
        const perChipDelay = reducedMotion ? 0 : idx * 100
        return (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: composite key with stable side+letter prefix; bounded length-4 tuple
            key={`reveal-${side}-${letter}-${idx}`}
            data-reveal-chip={`${side}-${idx}`}
            data-reveal-reduced-motion={reducedMotion ? 'true' : 'false'}
            style={{
              width: 36,
              height: 36,
              borderRadius: 'var(--radius-md)',
              background:
                side === 'own'
                  ? 'var(--color-surface-mid)'
                  : 'var(--color-surface-deep)',
              border: '2px solid var(--color-surface-stroke)',
              color: 'var(--color-inkwell)',
              fontFamily: 'var(--font-family-display)',
              fontWeight: 700,
              fontSize: 'var(--font-size-22)',
              lineHeight: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: reducedMotion ? 1 : side === 'own' ? 0.85 : 0,
              // CSS keyframe-driven reveal (keyframes shipped in
              // globals.css). Reduced motion: instant α=1.0 with no animation
              // (the hard timeout in WinOverlay still gates the reward grid).
              animation: reducedMotion
                ? undefined
                : `affinity-reveal-${side} 200ms cubic-bezier(0.4, 1.4, 0.6, 1) ${
                    startDelay + perChipDelay
                  }ms forwards`,
            }}
          >
            {letter.toUpperCase()}
          </div>
        )
      })}
    </div>
  )
}
